import { memo, useEffect, useRef, useMemo, useCallback, useState } from "react";
import { Link } from "react-router";

import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  type ChatMessage,
  createMessageComponents,
  extractLessonKeys,
  CUSTOM_PATH_DRAFT_PREFIX,
} from "sj-ui-toolkit/src/atlas/components/AspenChat";

import S from "./ChatMessageList.module.scss";

// Stable module-level reference — prevents ReactMarkdown from remounting renderers.
const messageComponents = createMessageComponents((href, children) => (
  <Link to={href.replace(/\\/g, "")} className={S.link}>
    {children}
  </Link>
));
const remarkPlugins = [remarkGfm];

// Show an active tool immediately, but keep its label long enough to be readable
// after it completes. A newer active tool always replaces it immediately.
const MIN_TOOL_PROGRESS_VISIBLE_MS = 2000;
const STATUS_ROTATION_INTERVAL_MS = 5_000;
const GENERIC_WORKING_LABEL = "Working…";
const THINKING_STATUS_MESSAGES = [
  GENERIC_WORKING_LABEL,
  "Reviewing the details…",
  "Putting that together…",
  "Doing the hard part so you don’t have to…",
  "Checking the work. Then checking it again…",
  "One step at a time. That’s how journeys work…",
  "Following the path…",
];

const TOOL_PROGRESS_LABELS: Record<string, string> = {
  assignments__find_assignments: "Finding assignments…",
  assignments__list_assignment_participants: "Tallying assignment participants…",
  content__get_lessons: "Loading lesson details…",
  content__list_tags: "Opening the tag library…",
  content__search_lessons: "Finding the right lesson for the job…",
  content__get_path: "Loading path details…",
  content__search_paths: "Discovering paths…",
  directory__lookup_users_by_email: "Looking for learners…",
  training__add_users_to_assignment: "Adding learners to the assignment…",
  training__remove_users_from_assignment: "Removing learners from the assignment…",
};

function toolProgressLabel(toolName: string): string {
  return TOOL_PROGRESS_LABELS[toolName] ?? GENERIC_WORKING_LABEL;
}

interface StreamingActivityProps {
  toolProgress: NonNullable<ChatMessage["toolProgress"]>;
  rotationIndex: number;
}

function StreamingActivity({ toolProgress, rotationIndex }: StreamingActivityProps) {
  const { toolName, pendingStart } = toolProgress;
  const consumedStartIdRef = useRef<string | null>(null);
  const [visibleTool, setVisibleTool] = useState<{ toolName: string; shownAt: number } | null>(
    null
  );

  useEffect(() => {
    if (!pendingStart || consumedStartIdRef.current === pendingStart.id) return;
    consumedStartIdRef.current = pendingStart.id;
    setVisibleTool({ toolName: pendingStart.toolName, shownAt: Date.now() });
  }, [pendingStart]);

  useEffect(() => {
    if (toolName || !visibleTool) return;
    const remaining = Math.max(
      0,
      MIN_TOOL_PROGRESS_VISIBLE_MS - (Date.now() - visibleTool.shownAt)
    );
    const timer = window.setTimeout(() => setVisibleTool(null), remaining);
    return () => window.clearTimeout(timer);
  }, [toolName, visibleTool]);

  const statusMessage = visibleTool
    ? toolProgressLabel(visibleTool.toolName)
    : THINKING_STATUS_MESSAGES[rotationIndex % THINKING_STATUS_MESSAGES.length];

  return (
    <div className={S.streamingActivity} data-testid="aspen-chat-streaming-activity">
      <span className={S.typingIndicator} aria-hidden="true">
        <span className={S.typingDot} />
        <span className={S.typingDot} />
        <span className={S.typingDot} />
      </span>
      <span className={S.streamingActivityText}>{statusMessage}</span>
    </div>
  );
}

interface ChatMessageItemProps {
  message: ChatMessage;
  rotationIndex: number;
}

const ChatMessageItem = memo(function ChatMessageItem({
  message,
  rotationIndex,
}: ChatMessageItemProps) {
  return (
    <div className={C(S.messageRow, message.role === "user" ? S.user : S.assistant)}>
      {message.role === "assistant" && (
        <div className={S.assistantAvatar} aria-hidden="true">
          <i className="pi pi-sparkles text-surface-950" style={{ fontSize: "0.65rem" }} />
        </div>
      )}
      <div className={C(S.messageBubble, message.role === "user" ? S.user : S.assistant)}>
        {message.role === "user" ? (
          message.content
        ) : (
          <ReactMarkdown remarkPlugins={remarkPlugins} components={messageComponents}>
            {message.content}
          </ReactMarkdown>
        )}
        {message.streaming && message.content && (
          <span className={S.streamingCursor} aria-hidden="true" />
        )}
        {message.streaming && message.toolProgress && (
          <StreamingActivity toolProgress={message.toolProgress} rotationIndex={rotationIndex} />
        )}
      </div>
    </div>
  );
});

interface ChatMessageListProps {
  messages: ChatMessage[];
  transcriptLoading: boolean;
  error: string | null;
  streaming: boolean;
}

export function ChatMessageList({
  messages,
  transcriptLoading,
  error,
  streaming,
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [rotationIndex, setRotationIndex] = useState(0);
  const wasStreamingRef = useRef(false);

  useEffect(() => {
    // Keep the tally across turns, advancing even when a turn ends before a timer tick.
    if (wasStreamingRef.current && !streaming) {
      setRotationIndex(index => index + 1);
    }
    wasStreamingRef.current = streaming;
    if (!streaming) return;

    const timer = window.setInterval(() => {
      setRotationIndex(index => index + 1);
    }, STATUS_ROTATION_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [streaming]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const lastAssistantMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === "assistant") return messages[i];
    }
    return null;
  }, [messages]);

  const lessonKeysInLastMessage = useMemo(
    () => (lastAssistantMessage ? extractLessonKeys(lastAssistantMessage.content) : []),
    [lastAssistantMessage]
  );

  const handleCreateCustomPath = useCallback(() => {
    if (!lessonKeysInLastMessage.length) return;
    const uuid = crypto.randomUUID();
    sessionStorage.setItem(
      `${CUSTOM_PATH_DRAFT_PREFIX}${uuid}`,
      JSON.stringify(lessonKeysInLastMessage)
    );
    window.location.assign(
      `${AdminAppRoutes.routes.paths.routes.new.route}?customPathDraft=${uuid}`
    );
  }, [lessonKeysInLastMessage]);

  return (
    <>
      {transcriptLoading && (
        <div className={S.transcriptLoading} role="status" aria-live="polite" aria-atomic="true">
          <ProgressSpinner style={{ width: "18px", height: "18px" }} strokeWidth="5" />
          Loading chat session...
        </div>
      )}

      <div
        className={C(S.messageLog, themeClasses.scrollbar)}
        role="log"
        aria-live="polite"
        aria-label="Conversation"
        data-testid="aspen-chat-log"
      >
        <div className={S.messageList}>
          {messages.map((message: ChatMessage) => (
            <ChatMessageItem
              key={message.id}
              message={message}
              rotationIndex={message.streaming ? rotationIndex : 0}
            />
          ))}
        </div>

        {error && <Message severity="error" text={error} className="w-full mt-4" />}

        {!streaming && !transcriptLoading && lessonKeysInLastMessage.length > 0 && (
          <div className={S.customPathContainer}>
            <Button
              label={`Create Custom Path from ${lessonKeysInLastMessage.length} lesson${lessonKeysInLastMessage.length === 1 ? "" : "s"}`}
              icon="pi pi-sitemap"
              onClick={handleCreateCustomPath}
              aria-label={`Create a custom path from the ${lessonKeysInLastMessage.length} lesson${lessonKeysInLastMessage.length === 1 ? "" : "s"} listed above`}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </>
  );
}
